'use strict';

define('vb/action/action',[
  'vb/private/constants',
  'vb/private/log',
  'vb/private/stateManagement/stateUtils',
  'vb/private/utils',
  'vb/private/action/actionChainUtils',
], (Constants, Log, StateUtils, Utils, ActionChainUtils) => {
  /** @typedef {{result: {payload: *, message: {summary: string}, error: Error}, name: string} | any} Outcome */

  const logger = Log.getLogger('/vb/action/action');

  /**
   * Base class for creating a custom action.
   * @category Action
   */
  class Action {
    /**
     * Constructs a new instance of the Action class.
     *
     * @param {string} id - A unique identifier for the action.
     * @param {string} [label] - A human-readable label for the action. Defaults to the `id` if not provided.
     */
    constructor(id, label) {
      /**
       * @protected
       * @type {Object}
       */
      this.log = logger;

      /**
       * add uniqueness for cases where this is invoked in a called action chain (thus
       * run many times)
       * @type {string}
       */
      this.id = `${id}_${Utils.generateUniqueId()}`;
      /**
       * @type {string}
       */
      this.label = label || this.id;
      /**
       * @protected
       * @type {string}
       */
      this.logLabel = (this.label === this.id) ? this.id : `${this.label} [${this.id}]`;
      /**
       * @type {string}
       */
      this.metadataKey = id; // the original key from the "actions" in the action chain
      /**
       * @type {string|null}
       */
      this.containerLifecycleState = null; // Defined in actionChain.js

      /**
       * options for this action
       * @protected
       * @type {Object}
       */
      this.options = {};

      /**
       * the helpers object injected in actionRunner.js
       * @protected
       * @type {Object}
       */
      this.helpers = {};
    }

    /**
     * Starts the action and adds default handling.
     *
     * @param {Object} parameters - Parameters required to start the action.
     * @returns {Promise} A promise that resolves when the action completes successfully, or rejects with an error.
     * @throws {Error} If the action is cancelled due to page exit or navigation.
     */
    start(parameters) {
      // the promise executor function is called immediately (before the Promise constructor returns),
      // so there should be no difference checking isCancelled() outside of the executor
      if (this.isCancelled()) {
        // prevent the action from starting if it's in a cancelled state
        return Promise.reject(new Error('cancelled because the page has exited due to navigation'));
      }

      // call the action - if its a promise, treat it as such, otherwise, just resolve
      return Promise.resolve().then(() => this.perform(parameters))
        .catch((e) => {
          this.log.error('Action', this.logLabel, 'failed.', e);
          let err = e || new Error('error in executing action');
          if (typeof e === 'string') {
            err = new Error(e);
          }
          throw err;
        });
    }

    /**
     * Performs an action. This can either run synchronously (returning an outcome) or return
     * a promise (which resolves to an outcome). The 'createOutcome' utility method can be used
     * to generate the result in either implementation.
     *
     * If this returns a promise, rejecting without an outcome will result in the action chain
     * terminating (TODO: we don't yet do this =)).
     *
     * @param {Object} parameters
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    perform(parameters) {
      throw new TypeError('Subclass needs to override execute method.');
    }

    /**
     * Set options for this action.
     *
     * @param {Object} options options to set
     */
    setOptions(options) {
      this.options = Object.assign(this.options, options);
    }

    /**
     * This method is used to check if the action is in a cancelled state. An action is cancelled when the underlying
     * page, for example, has exited due to navigation to a different page. This method is used to immediately reject
     * an action before it starts and thus terminating the action chain. An in-flight action can also use this method
     * to terminate itself to prevent unwanted side effects.
     *
     * @returns {boolean}
     */
    isCancelled() {
      // an action chain is cancelled when the underlying container has exited
      return ActionChainUtils.isContainerInactive(this.containerLifecycleState);
    }

    /**
     * An object containing possible action outcome names.
     *
     * @static
     * @readonly
     * @type {Object}
     */
    static get Outcomes() {
      return Constants.ActionOutcomes;
    }

    /**
     * An object containing possible action outcome names. This has been deprecated in favor of
     * {@link Action.Outcomes}.
     *
     * @static
     * @readonly
     * @type {Object}
     * @deprecated in favor of {@link Action.Outcomes}.
     */
    static get Outcome() {
      logger.warn('Action.Outcome has been deprecated. Please use Action.Outcomes instead.');
      return this.Outcomes;
    }

    /**
     * Evaluates and builds the parameter value based on the provided key, value, and available contexts.
     *
     * This callback method allows actions to customize how their parameters are evaluated during execution.
     * It delegates the evaluation process to the `StateUtils.buildVariableDefaults` method.
     *
     * @param {string} key - The parameter key being evaluated.
     * @param {*} value - The parameter value to be evaluated.
     * @param {Object} availableContexts - An object containing available context information.
     * @returns {*} The evaluated parameter value.
     */
    static buildParamValue(key, value, availableContexts) {
      return StateUtils.buildVariableDefaults(key, availableContexts, 'any', value);
    }

    /**
     * Creates an outcome object with the specified name and result.
     *
     * @param {string} name - The name of the outcome, which determines the next action.
     * @param {*} [result] - Optional payload for the result.
     * @returns {Outcome} - The created outcome object.
     */
    static createOutcome(name, result) {
      return { name, result };
    }

    /**
     * Creates a successful outcome object with the provided result.
     *
     * @param {*} [result] - The optional payload for the result.
     * @returns {Outcome} - The created outcome object with a name of 'SUCCESS'.
     */
    static createSuccessOutcome(result) {
      return { name: this.Outcomes.SUCCESS, result };
    }

    /**
     * Utility method to create a 'failure' outcome.
     *
     * @param {string} [summary] The optional summary error message, used as the 'summary' in the Message object.
     * @param {Error|*} [error] The optional Error/exception object
     * @param {*} [payload] The optional payload for the result, (outcome.result.payload)
     * @returns {Outcome}
     */
    static createFailureOutcome(summary, error, payload) {
      const result = {
        // todo: this should eventually match the JET Message class {detail, severity, summary}
        message: {
          summary,
        },
        error,
        payload,
      };
      return { name: this.Outcomes.FAILURE, result };
    }
  }

  return Action;
});

